/**
 * Bid Optimizer results view — shared by the detail route
 * (pages/bid-optimizer/[id]/index.tsx) for completed jobs. Purely
 * presentational: the parent route owns the single combined fetch
 * (status + output) and passes the parsed columns/rows/jobConfigData down,
 * so this component never shows its own loading spinner — there is exactly
 * one loader per page view, not one here plus one in the parent.
 *
 * Ports the full logic from the old flow's ResultStep.tsx (the more
 * complete of the two old result views — it has the controlled-filter /
 * row-selection refinements the old id/index.tsx dashboard lacked):
 *   - Same column derivation (Bid Value sorted last, renamed "Bid Multiplier",
 *     per-column filter option lists, numeric-aware sort) — done by the
 *     parent via parseBidMultiplierOutput.ts.
 *   - dataMappingConfig.mappings.bidMultiplier.columnMapping (from jobConfigData)
 *     drives the DMA/region key lookup for the apply-bids payload.
 *   - Facebook / Google "Apply Bids" forms building the same per-row payload
 *     shape, POSTing to /api/bid-multiplier/{id} (now a real, if stubbed,
 *     backend endpoint — shows the actual response instead of faking success).
 *
 * All chrome rebuilt with the app's CSS-var theme tokens — no antd/daisyUI.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { DMA_LIST } from "../../../constants/DMAList";
import { LOCALE_LIST } from "../../../constants/LocaleList";
import { BidOptimizerColumn, BidOptimizerRow } from "./parseBidMultiplierOutput";

interface BidOptimizerResultsProps {
  jobConfigId: string;
  name?: string;
  jobConfigData: any;
  columns: BidOptimizerColumn[];
  rows: BidOptimizerRow[];
}

type SortDir = "asc" | "desc" | null;

const BidOptimizerResults: React.FC<BidOptimizerResultsProps> = ({ jobConfigId, name, jobConfigData, columns, rows }) => {
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const [sort, setSort] = useState<{ col: string; dir: SortDir }>({ col: "Bid Value", dir: "desc" });

  const [activeTab, setActiveTab] = useState<"facebook" | "google">("facebook");
  const [fbForm, setFbForm] = useState({ controlAdsetId: "", controlBudget: "", testAdsetId: "", testBudget: "" });
  const [ggForm, setGgForm] = useState({ controlCampaignId: "", controlBudget: "", testCampaignId: "", testBudget: "" });
  const [fbLoading, setFbLoading] = useState(false);
  const [ggLoading, setGgLoading] = useState(false);
  const [applyResult, setApplyResult] = useState<{ ok: boolean; message: string } | null>(null);

  const columnMapping = jobConfigData?.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping || {};

  const getRegionKey = (region: any) => DMA_LIST.find(i => i.name === region)?.key;
  const getLocaleKey = (locale: any) => LOCALE_LIST.find(i => i.name === locale)?.key;
  const getRegionKeyGoogle = (region: any) => DMA_LIST.find(i => i.name === region)?.gg_ads_key;
  const getLocaleKeyGoogle = (locale: any) => LOCALE_LIST.find(i => i.name === locale)?.gg_locale;

  // ── Filtering + sorting ──────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    let out = rows.filter(r => Object.entries(activeFilters).every(([col, values]) => {
      if (!values.length) return true;
      const raw = r[col];
      const formatted = typeof raw === "number" ? raw.toFixed(col === "Bid Value" ? 2 : 0) : String(raw);
      return values.includes(formatted) || values.includes(String(raw));
    }));

    if (sort.dir) {
      out = [...out].sort((a, b) => {
        const av = a[sort.col], bv = b[sort.col];
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, activeFilters, sort]);

  const toggleSort = (col: string) => {
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === "desc" ? "asc" : prev.dir === "asc" ? null : "desc" }
      : { col, dir: "desc" });
  };

  const toggleFilterValue = (col: string, value: string) => {
    setActiveFilters(prev => {
      const cur = prev[col] || [];
      const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
      const updated = { ...prev, [col]: next };
      if (next.length === 0) delete updated[col];
      return updated;
    });
  };

  const removeFilter = (col: string, value: string) => toggleFilterValue(col, value);

  const toggleRow = (idx: number) => setSelectedRows(prev => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const selectAll = () => setSelectedRows(new Set(filteredRows.map(r => r.__idx)));
  const clearAll = () => setSelectedRows(new Set());

  const buildPayloadRows = (provider: "facebookads" | "googleads") => {
    const selected = rows.filter(r => selectedRows.has(r.__idx));
    return selected.map(item => ({
      age: columnMapping?.age_varialbes?.length ? item[columnMapping.age_varialbes[0]] : item.age,
      devicePlatform: columnMapping?.device_platform_varialbes?.length ? item[columnMapping.device_platform_varialbes[0]] : undefined,
      gender: columnMapping?.gender_varialbes?.length ? item[columnMapping.gender_varialbes[0]] : undefined,
      locale: columnMapping?.locale_varialbes?.length
        ? (provider === "facebookads" ? getLocaleKey(item[columnMapping.locale_varialbes[0]]) : getLocaleKeyGoogle(item[columnMapping.locale_varialbes[0]]))
        : undefined,
      regions: columnMapping?.region_varialbes?.length
        ? (provider === "facebookads" ? getRegionKey(item[columnMapping.region_varialbes[0]]) : getRegionKeyGoogle(item[columnMapping.region_varialbes[0]]))
        : undefined,
      salesScaled: item["Bid Value"],
      userDevice: columnMapping?.user_devices_varialbes?.length ? item[columnMapping.user_devices_varialbes[0]] : undefined,
      userOS: columnMapping?.user_os_varialbes?.length ? item[columnMapping.user_os_varialbes[0]] : undefined,
    }));
  };

  const applyBids = async (provider: "facebookads" | "googleads") => {
    setApplyResult(null);
    if (selectedRows.size === 0) {
      setApplyResult({ ok: false, message: "Please select at least one row to apply bids." });
      return;
    }
    const isFb = provider === "facebookads";
    const form = isFb ? fbForm : ggForm;
    if (Object.values(form).some(v => !v)) {
      setApplyResult({ ok: false, message: "Please fill in all fields." });
      return;
    }

    const payload: Record<string, any> = {
      jobConfigId,
      provider,
      payload: buildPayloadRows(provider),
    };
    if (isFb) {
      payload.controlAdsetId = fbForm.controlAdsetId;
      payload.controlBudget = parseFloat(fbForm.controlBudget);
      payload.testAdsetId = fbForm.testAdsetId;
      payload.testBudget = parseFloat(fbForm.testBudget);
    } else {
      payload.controlCampaignId = ggForm.controlCampaignId;
      payload.controlBudget = parseFloat(ggForm.controlBudget);
      payload.testCampaignId = ggForm.testCampaignId;
      payload.testBudget = parseFloat(ggForm.testBudget);
    }

    (isFb ? setFbLoading : setGgLoading)(true);
    try {
      const res = await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      setApplyResult({ ok: true, message: res.data?.message || `Bids applied to ${isFb ? "Facebook" : "Google"} successfully.` });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setApplyResult({ ok: false, message: `Failed to apply bids: ${msg}` });
    } finally {
      (isFb ? setFbLoading : setGgLoading)(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1280, margin: "0 auto", fontFamily: "var(--font-sans)" }}>
      <button onClick={() => navigate("/bid-optimizer")} style={linkBtnStyle}>← Back to list</button>

      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "20px 0 4px" }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>{name || "Bid Optimizer Results"}</h1>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 20px" }}>
        Review the bid multiplier results and apply them to your ad platform.
      </p>

      {/* ── Active filters ── */}
      {Object.entries(activeFilters).some(([, v]) => v.length > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {Object.entries(activeFilters).flatMap(([col, values]) => values.map(v => (
            <span key={`${col}-${v}`} style={{ ...chipStyle, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {columns.find(c => c.key === col)?.label || col}: {v}
              <span onClick={() => removeFilter(col, v)} style={{ cursor: "pointer", color: "var(--text-tertiary)" }}>✕</span>
            </span>
          )))}
        </div>
      )}

      {/* ── Results table ── */}
      <div style={{ borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)", background: "var(--bg-surface)", marginBottom: 24, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-primary)" }}>
            {selectedRows.size} of {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"} selected
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={selectAll} style={miniBtnStyle}>Select All</button>
            <button onClick={clearAll} style={miniBtnStyle}>Clear All</button>
          </div>
        </div>

        <div style={{ overflowX: "auto", maxHeight: 460 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36 }} />
                {columns.map(col => (
                  <th key={col.key} style={thStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
                      <span onClick={() => toggleSort(col.key)} style={{ cursor: "pointer", userSelect: "none" }}>
                        {col.label}
                        {sort.col === col.key && sort.dir && (sort.dir === "asc" ? " ↑" : " ↓")}
                      </span>
                      <span
                        onClick={() => setOpenFilterCol(openFilterCol === col.key ? null : col.key)}
                        style={{ cursor: "pointer", fontSize: 10, color: activeFilters[col.key]?.length ? "var(--blue)" : "var(--text-tertiary)" }}
                      >
                        ▾
                      </span>
                      {openFilterCol === col.key && (
                        <div style={filterDropdownStyle} onClick={e => e.stopPropagation()}>
                          {col.filterOptions.map(opt => (
                            <label key={opt} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                              <input
                                type="checkbox"
                                checked={activeFilters[col.key]?.includes(opt) || false}
                                onChange={() => toggleFilterValue(col.key, opt)}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(row => (
                <tr key={row.__idx} style={{ background: selectedRows.has(row.__idx) ? "rgba(59,130,246,0.06)" : "transparent" }}>
                  <td style={tdStyle}>
                    <input type="checkbox" checked={selectedRows.has(row.__idx)} onChange={() => toggleRow(row.__idx)} />
                  </td>
                  {columns.map(col => (
                    <td key={col.key} style={tdStyle}>
                      {col.isBidValue && typeof row[col.key] === "number" ? row[col.key].toFixed(6) : String(row[col.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign: "center", color: "var(--text-tertiary)", padding: 24 }}>No rows match the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Apply bids result banner ── */}
      {applyResult && (
        <div style={{
          marginBottom: 16, padding: "10px 14px", borderRadius: "var(--radius)",
          background: applyResult.ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
          border: `1px solid ${applyResult.ok ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
          color: applyResult.ok ? "#10b981" : "#ef4444", fontSize: 12.5,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {applyResult.message}
          <button onClick={() => setApplyResult(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: "inherit", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {/* ── Apply bids tabs ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {(["facebook", "google"] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "6px 16px", borderRadius: "var(--radius)", fontSize: 12.5, fontWeight: 500,
              background: activeTab === tab ? "var(--blue-subtle)" : "var(--bg-surface)",
              color: activeTab === tab ? "var(--blue)" : "var(--text-secondary)",
              border: `1px solid ${activeTab === tab ? "var(--blue)" : "var(--border-default)"}`,
              cursor: "pointer", textTransform: "capitalize",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <div style={{ borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)", background: "var(--bg-surface)", padding: 18 }}>
        {activeTab === "facebook" ? (
          <ApplyForm
            rows={[
              { label: "Control", idKey: "controlAdsetId", budgetKey: "controlBudget", idPlaceholder: "Enter Control Adset ID" },
              { label: "Test", idKey: "testAdsetId", budgetKey: "testBudget", idPlaceholder: "Enter Test Adset ID" },
            ]}
            values={fbForm}
            onChange={(k, v) => setFbForm(prev => ({ ...prev, [k]: v }))}
            onSubmit={() => applyBids("facebookads")}
            loading={fbLoading}
            label="Apply Facebook Bids"
          />
        ) : (
          <ApplyForm
            rows={[
              { label: "Control", idKey: "controlCampaignId", budgetKey: "controlBudget", idPlaceholder: "Enter Control Campaign ID" },
              { label: "Test", idKey: "testCampaignId", budgetKey: "testBudget", idPlaceholder: "Enter Test Campaign ID" },
            ]}
            values={ggForm}
            onChange={(k, v) => setGgForm(prev => ({ ...prev, [k]: v }))}
            onSubmit={() => applyBids("googleads")}
            loading={ggLoading}
            label="Apply Google Bids"
          />
        )}
      </div>
    </div>
  );
};

// ── Apply form ───────────────────────────────────────────────────────────────

const ApplyForm: React.FC<{
  rows: { label: string; idKey: string; budgetKey: string; idPlaceholder: string }[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSubmit: () => void;
  loading: boolean;
  label: string;
}> = ({ rows, values, onChange, onSubmit, loading, label }) => (
  <div>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginBottom: 14 }}>
      <thead>
        <tr>
          <th style={thStyle}>Type</th>
          <th style={thStyle}>ID</th>
          <th style={thStyle}>Budget</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.label}>
            <td style={tdStyle}>{r.label}</td>
            <td style={tdStyle}>
              <input value={values[r.idKey] || ""} onChange={e => onChange(r.idKey, e.target.value)} placeholder={r.idPlaceholder} style={inputStyle} />
            </td>
            <td style={tdStyle}>
              <input value={values[r.budgetKey] || ""} onChange={e => onChange(r.budgetKey, e.target.value)} placeholder="Enter Budget" style={inputStyle} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "center" }}>
      <button onClick={onSubmit} disabled={loading} style={primaryBtnStyle}>
        {loading ? "Applying…" : label}
      </button>
    </div>
  </div>
);

// ── Styles ───────────────────────────────────────────────────────────────────

const chipStyle: React.CSSProperties = {
  padding: "3px 9px", borderRadius: 12, background: "var(--bg-hover)",
  color: "var(--text-secondary)", fontSize: 11.5, fontFamily: "var(--font-sans)",
};
const thStyle: React.CSSProperties = {
  position: "sticky", top: 0, textAlign: "left", padding: "9px 12px",
  background: "var(--bg-hover)", color: "var(--text-secondary)",
  borderBottom: "1px solid var(--border-default)", whiteSpace: "nowrap", fontWeight: 500,
};
const tdStyle: React.CSSProperties = {
  padding: "8px 12px", color: "var(--text-primary)",
  borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
};
const filterDropdownStyle: React.CSSProperties = {
  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30,
  background: "var(--bg-elevated)", border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-sm)", boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
  maxHeight: 220, overflowY: "auto", minWidth: 140, padding: "4px 0",
};
const miniBtnStyle: React.CSSProperties = {
  padding: "5px 12px", borderRadius: "var(--radius-sm)", background: "var(--bg-hover)",
  color: "var(--text-secondary)", border: "1px solid var(--border-default)", fontSize: 12, cursor: "pointer",
};
const linkBtnStyle: React.CSSProperties = {
  padding: "7px 14px", borderRadius: "var(--radius)", background: "none",
  color: "var(--text-tertiary)", border: "1px solid var(--border-default)",
  fontSize: 12, cursor: "pointer", fontFamily: "var(--font-sans)",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "6px 9px", borderRadius: "var(--radius-sm)",
  border: "1px solid var(--border-default)", background: "var(--bg-base)",
  color: "var(--text-primary)", fontSize: 12.5, fontFamily: "var(--font-sans)",
};
const primaryBtnStyle: React.CSSProperties = {
  padding: "9px 22px", borderRadius: "var(--radius)", background: "var(--blue)",
  color: "#fff", border: "none", fontSize: 13, fontWeight: 500, cursor: "pointer",
};

export default BidOptimizerResults;
